import { useState, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Mic, MicOff, Square } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface IntegratedVoiceChatProps {
  isActive: boolean;
  onToggle: () => void;
  onVoiceMessage: (text: string) => void;
  onPlayResponse: (text: string) => void;
}

export const IntegratedVoiceChat = ({ 
  isActive, 
  onToggle, 
  onVoiceMessage, 
  onPlayResponse 
}: IntegratedVoiceChatProps) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  const { toast } = useToast();

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: { 
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true 
        } 
      });

      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await processAudio(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      toast({
        title: "Recording Failed",
        description: "Could not access microphone",
        variant: "destructive",
      });
    }
  }, [toast]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, [isRecording]);

  const processAudio = async (audioBlob: Blob) => {
    try {
      console.log('🎤 Processing audio blob, size:', audioBlob.size);
      
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64Audio = (reader.result as string).split(',')[1];
          console.log('🔄 Calling voice-to-text function with audio length:', base64Audio.length);
          
          const { data, error } = await supabase.functions.invoke('voice-to-text', {
            body: { audio: base64Audio }
          });

          console.log('📞 Voice-to-text response:', { data, error });

          if (error) {
            console.error('❌ Voice-to-text error:', error);
            throw error;
          }
          
          if (data?.text) {
            console.log('✅ Transcribed text:', data.text);
            onVoiceMessage(data.text);
          } else {
            console.warn('⚠️ No text received from voice-to-text');
            toast({
              title: "No Speech Detected",
              description: "Please try speaking more clearly",
              variant: "default",
            });
          }
        } catch (processingError) {
          console.error('❌ Audio processing error:', processingError);
          throw processingError;
        }
      };
      
      reader.onerror = (readerError) => {
        console.error('❌ FileReader error:', readerError);
        throw new Error('Failed to read audio file');
      };
      
      reader.readAsDataURL(audioBlob);
    } catch (error) {
      console.error('❌ Error processing audio:', error);
      toast({
        title: "Processing Failed",
        description: "Could not process voice message. Please try again.",
        variant: "destructive",
      });
    }
  };

  const playResponse = async (text: string) => {
    try {
      setIsPlaying(true);
      
      const { data, error } = await supabase.functions.invoke('text-to-speech', {
        body: { text }
      });

      if (error) throw error;

      if (data.audioBase64) {
        const audioBlob = new Blob([
          Uint8Array.from(atob(data.audioBase64), c => c.charCodeAt(0))
        ], { type: 'audio/mpeg' });
        
        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);
        currentAudioRef.current = audio;
        
        audio.onended = () => {
          setIsPlaying(false);
          URL.revokeObjectURL(audioUrl);
          currentAudioRef.current = null;
        };
        
        audio.onerror = () => {
          setIsPlaying(false);
          URL.revokeObjectURL(audioUrl);
          currentAudioRef.current = null;
        };
        
        await audio.play();
        onPlayResponse(text);
      }
    } catch (error) {
      console.error('Error playing response:', error);
      setIsPlaying(false);
      toast({
        title: "Playback Failed",
        description: "Could not play voice response",
        variant: "destructive",
      });
    }
  };

  const stopPlayback = () => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
      setIsPlaying(false);
    }
  };

  if (!isActive) return null;

  return (
    <div className="flex items-center gap-2">
      {isRecording ? (
        <Button
          onClick={stopRecording}
          size="sm"
          variant="destructive"
          className="flex items-center gap-2"
        >
          <Square className="h-4 w-4" />
          Stop Recording
        </Button>
      ) : (
        <Button
          onClick={startRecording}
          size="sm"
          variant="outline"
          className="flex items-center gap-2"
        >
          <Mic className="h-4 w-4" />
          Record
        </Button>
      )}
      
      {isPlaying && (
        <Button
          onClick={stopPlayback}
          size="sm"
          variant="outline"
          className="flex items-center gap-2"
        >
          <MicOff className="h-4 w-4" />
          Stop Audio
        </Button>
      )}
    </div>
  );
};